/**
 * MedicationCard — one searched drug's Swiss medication (SAI) record, shown
 * as a structured info card in the sidebar. This is the answer to "search a
 * drug, see its info": active ingredient(s), ATC class, and the
 * Fachinformation sections — pulled straight from SAI, no agent prose.
 *
 * Owns its four states per the resilience rule: loading (spinner), error
 * (retryable-by-retyping message), not-found (plain factual line), and data
 * (the card). The header always shows what the nurse typed so the card is
 * identifiable before its data resolves.
 *
 * Summary-first, expand-on-demand
 * ───────────────────────────────
 * DELIBERATE PRODUCT DECISION (Joël, 2026-06-06): the prior version rendered
 * every Fachinformation section in full and a comment framed any collapse as
 * a "bedside-safety hazard". That was overturned on purpose — the wall of
 * text was the actual complaint ("too comprehensive; summarise like chat,
 * summary first with option to extend"). So:
 *
 *   - The identity fields (trade/generic name, active ingredients, ATC, the
 *     Fachinformation LINK, source tier) ARE the summary — always visible,
 *     answering "what is this drug + where's the source?" at a glance.
 *   - The Fachinformation prose (dosage, indications, contraindications) is
 *     collapsed behind a disclosure with a 2-line teaser of the first section.
 *
 * This is collapse, NOT truncation: the FULL content is one tap away and
 * nothing is silently dropped, and the authoritative-source link is never
 * gated. The medication service already bounds the chunk count, so the
 * expanded view is a handful of sections, not infinite scroll. (Default-expand
 * was considered and rejected — it reinstates the wall of text the decision
 * exists to remove.)
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Spinner } from '@/components/ui/spinner'
import type { MedicationLookup } from '@frontend/hooks/useMedicationSearch'
import type { MedicationInfo } from '@frontend/api/endpoints/medications'

export function MedicationCard({ lookup }: { lookup: MedicationLookup }) {
  const { t } = useTranslation()
  const { term, data, isLoading, isError } = lookup
  const [expanded, setExpanded] = useState(false)

  const shell =
    'rounded-[12px] border border-[var(--divider)] bg-[var(--canvas)] px-4 py-3.5'

  if (isLoading) {
    return (
      <div className={shell}>
        <CardHeading title={term} />
        <div className="mt-2 flex items-center gap-2 text-[13px] text-neutral-500">
          <Spinner className="h-3.5 w-3.5 text-neutral-400" />
          <span>{t('compatibility.medication.loading')}</span>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={shell}>
        <CardHeading title={term} />
        <p className="mt-2 text-[13px] leading-snug text-neutral-500">
          {t('compatibility.medication.error')}
        </p>
      </div>
    )
  }

  const drug: MedicationInfo | undefined = data?.results?.[0]
  if (!drug) {
    return (
      <div className={shell}>
        <CardHeading title={term} />
        <p className="mt-2 text-[13px] leading-snug text-neutral-500">
          {t('compatibility.medication.notFound')}
        </p>
      </div>
    )
  }

  const showGeneric =
    !!drug.medication_name &&
    drug.medication_name.toLowerCase() !== drug.trade_name.toLowerCase()

  return (
    <div className={shell}>
      <CardHeading title={drug.trade_name || term} />
      {showGeneric && (
        <p className="mt-0.5 text-[12px] font-semibold text-neutral-600">
          {drug.medication_name}
        </p>
      )}

      {drug.active_ingredients.length > 0 && (
        <div className="mt-2.5">
          <p className="font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
            {t('compatibility.medication.ingredients')}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {drug.active_ingredients.map((ing) => (
              <span
                key={ing}
                className="rounded-full border border-[var(--hairline)] bg-neutral-50 px-2.5 py-0.5 text-[12px] text-neutral-700"
              >
                {ing}
              </span>
            ))}
          </div>
        </div>
      )}

      {drug.atc_code && (
        <p className="mt-2.5 font-mono text-[11px] text-neutral-500">
          {t('compatibility.panel.atc')} {drug.atc_code}
        </p>
      )}

      {/* Fachinformation link stays in the SUMMARY (always visible, one tap) —
          it is the authoritative external source, so collapsing the prose
          sections must not also bury the link to the real document. */}
      {drug.url && (
        <a
          href={drug.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2.5 flex items-center gap-1.5 text-[13px] font-semibold text-primary-600 transition hover:text-primary-700"
        >
          {t('compatibility.medication.fachinfo')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      )}

      {/* Fachinformation sections — summary-first. Collapsed shows a 2-line teaser of
          the first section; the disclosure reveals every section in full. The
          identity fields above are the at-a-glance summary; this is the
          "extend" the nurse opts into, not a default wall of text. */}
      {drug.sections.length > 0 &&
        (expanded ? (
          <div className="mt-3 space-y-2.5">
            {drug.sections.map((s, i) => (
              <div key={i}>
                {s.section && (
                  <p className="font-body text-[11px] font-semibold uppercase tracking-[0.05em] text-neutral-400">
                    {s.section}
                  </p>
                )}
                <p className="mt-0.5 whitespace-pre-line text-[13px] leading-relaxed text-neutral-700">
                  {s.content.trim()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3">
            {drug.sections[0].section && (
              <p className="font-body text-[11px] font-semibold uppercase tracking-[0.05em] text-neutral-400">
                {drug.sections[0].section}
              </p>
            )}
            <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-[13px] leading-relaxed text-neutral-700">
              {drug.sections[0].content.trim()}
            </p>
          </div>
        ))}

      {drug.sections.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-semibold text-primary-600 transition active:scale-95 hover:text-primary-700"
        >
          {expanded
            ? t('compatibility.medication.showLess')
            : t('compatibility.medication.showMore', { count: drug.sections.length })}
          <ChevronDown
            className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
            aria-hidden
          />
        </button>
      )}

      {/* Provenance — which knowledge tier answered (national/SAI,
          international/dm+d, …). The nurse must always see WHERE the data came
          from, especially when a drug escalates to a foreign source. Localised
          by tier; the backend's English source name is the fallback. */}
      {data?.source?.tier && (
        <p className="mt-3 border-t border-[var(--divider)] pt-2 font-body text-[11px] text-neutral-400">
          {t('compatibility.medication.sourceLabel', {
            source: t(
              `compatibility.medication.tier.${data.source.tier}`,
              data.source.name,
            ),
          })}
        </p>
      )}
    </div>
  )
}

function CardHeading({ title }: { title: string }) {
  return (
    <p className="font-display text-[15px] font-semibold leading-snug tracking-[-0.2px] text-neutral-900">
      {title}
    </p>
  )
}
